import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AppLayout } from '../components/AppLayout'
import { sharesApi, type ShareSummary } from '../api/endpoints'
import { ApiError } from '../api/types'
import { toast } from '../lib/toast'
import { watchAuthState } from '../lib/firebase'

/**
 * /shares — the signed-in user's own "share with my markup" lists. Broker
 * self-service: see every share you've created, copy its link, open it, or
 * delete it. Shares are created from the catalog table's row selection
 * (Create share) — this page is where they live afterwards.
 */
export function MyShares() {
  const [shares, setShares] = useState<ShareSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // AUTH-DRIVEN, not mount-driven. The one-shot fetch left the previous
  // user's shares rendered after sign-out (Tej 2026-09-20) — this page can
  // outlive the session (back button, open tab), so it follows the auth
  // state: signed in ⇒ fetch, signed out ⇒ clear immediately.
  useEffect(() => {
    let cancelled = false
    const unsub = watchAuthState((user) => {
      if (cancelled) return
      if (!user) {
        setShares([])
        setError('Sign in to see your shares.')
        return
      }
      sharesApi.mine()
        .then((r) => { if (!cancelled) { setShares(r.shares); setError(null) } })
        .catch((e) => {
          if (cancelled) return
          setError(e instanceof ApiError && e.status === 401
            ? 'Sign in to see your shares.'
            : 'Could not load your shares.')
          setShares([])
        })
    })
    return () => { cancelled = true; unsub() }
  }, [])

  async function copyLink(id: string) {
    const url = `${window.location.origin}/share/${id}`
    try { await navigator.clipboard.writeText(url); setCopiedId(id); toast('Link copied', 'success') }
    catch { toast('Could not copy — open the link and copy from the address bar.', 'error') }
  }

  async function remove(id: string) {
    try {
      await sharesApi.remove(id)
      setShares((prev) => (prev ? prev.filter((s) => s.shareId !== id) : prev))
      toast('Share deleted', 'success')
    } catch {
      toast('Could not delete — try again.', 'error')
    }
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8 sm:py-10">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold">Broker</p>
            <h1 className="mt-1 font-serif text-3xl font-bold tracking-tight text-ink">My shares</h1>
            <p className="mt-1 text-sm text-soft">Lists you've shared at your own markup.</p>
          </div>
          <Link
            to="/search"
            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full bg-navy px-5 text-sm font-semibold text-white no-underline hover:bg-navy-hover active:scale-[0.98] transition-all"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New share
          </Link>
        </div>

        <div className="mt-8">
          {shares === null ? (
            <div className="space-y-3" aria-hidden>
              {[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-xl skeleton" />)}
            </div>
          ) : error ? (
            <div className="rounded-card border border-danger/30 bg-danger/5 p-6 text-sm text-danger">{error}</div>
          ) : shares.length === 0 ? (
            <div className="rounded-card border border-border bg-paper p-10 text-center">
              <p className="font-serif text-xl text-ink">No shares yet</p>
              <p className="mt-2 text-sm text-soft">
                Open the catalog, tick the stones you want, and tap{' '}
                <span className="font-semibold text-ink">Create share</span> to make a link + Excel at your price.
              </p>
              <Link to="/search" className="mt-4 inline-flex btn-primary px-6 no-underline">Browse diamonds</Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {shares.map((s) => (
                <li
                  key={s.shareId}
                  className="rounded-xl border border-line bg-paper p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                  data-testid="my-share-row"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink truncate">
                      {s.title || `${s.count} stone${s.count === 1 ? '' : 's'}`}
                    </p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-soft">
                      <span className="tabular">{s.count} stone{s.count === 1 ? '' : 's'}</span>
                      <span aria-hidden>·</span>
                      <span className="tabular">+{s.markupPercent}% on {s.markupBase === 'aska' ? 'Aska price' : 'supplier price'}</span>
                      <span aria-hidden>·</span>
                      <span>{s.showStockId ? 'IDs shown' : 'IDs hidden'}</span>
                      {s.createdAt > 0 && (<><span aria-hidden>·</span><span>{new Date(s.createdAt).toLocaleDateString()}</span></>)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button" onClick={() => void copyLink(s.shareId)}
                      className="inline-flex h-9 items-center rounded-full border border-border bg-paper px-3.5 text-[12px] font-semibold text-ink hover:border-gold/50 hover:bg-cream transition-colors"
                      data-testid="my-share-copy"
                    >
                      {copiedId === s.shareId ? 'Copied' : 'Copy link'}
                    </button>
                    <a
                      href={`/share/${s.shareId}`} target="_blank" rel="noopener"
                      className="inline-flex h-9 items-center rounded-full bg-navy px-3.5 text-[12px] font-semibold text-white no-underline hover:bg-navy-hover transition-colors"
                    >
                      Open
                    </a>
                    <button
                      type="button" onClick={() => void remove(s.shareId)}
                      aria-label="Delete share"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-soft hover:text-danger hover:bg-page transition-colors"
                      data-testid="my-share-delete"
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
